import { apiFetch } from '../utils/apiClient';

/**
 * Typed client for `/user-teams/*`. User-teams are named groupings of users
 * for filtering the task list (ViewerToggle). Orthogonal to access-group
 * memberships (ADR 0009) — creating/renaming a user-team does NOT change
 * what tasks anyone can see. Mutations are super-admin only (enforced
 * server-side); list is callable by anyone authenticated.
 */

export interface UserTeam {
  teamId: string;
  displayName: string;
  color: string;
  members: string[];
}

export interface CreateUserTeamInput {
  displayName: string;
  color: string;
  id?: string;
}

export async function listUserTeams(): Promise<UserTeam[]> {
  const { userTeams } = await apiFetch<{ userTeams: UserTeam[] }>('/user-teams');
  return userTeams;
}

export async function createUserTeam(input: CreateUserTeamInput): Promise<UserTeam> {
  const { userTeam } = await apiFetch<{ userTeam: UserTeam }>('/user-teams', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return userTeam;
}

export async function updateUserTeam(
  teamId: string,
  patch: { displayName?: string; color?: string }
): Promise<UserTeam> {
  const { userTeam } = await apiFetch<{ userTeam: UserTeam }>(
    `/user-teams/${encodeURIComponent(teamId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }
  );
  return userTeam;
}

export async function deleteUserTeam(teamId: string): Promise<void> {
  await apiFetch<void>(`/user-teams/${encodeURIComponent(teamId)}`, {
    method: 'DELETE',
  });
}

export async function addUserTeamMember(teamId: string, sub: string): Promise<void> {
  await apiFetch<void>(`/user-teams/${encodeURIComponent(teamId)}/members`, {
    method: 'POST',
    body: JSON.stringify({ sub }),
  });
}

export async function removeUserTeamMember(teamId: string, sub: string): Promise<void> {
  await apiFetch<void>(
    `/user-teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(sub)}`,
    { method: 'DELETE' }
  );
}
